import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../css/WorkspaceOnboarding.css";

/**
 * WorkspaceOnboardingModal
 *
 * Full-screen modal shown to new workspace members when onboarding is configured.
 * All sections are optional — only rendered when the relevant config is present.
 *
 * Props:
 *   config        – the onboarding config object from the API
 *   state         – the user's current onboarding state from the API
 *   workspaceId   – workspace DB id
 *   customDomain  – workspace custom domain for navigation
 *   userInfo      – Firebase auth user
 *   onDismiss     – called after "Got it" (permanent dismiss)
 *   onSkip        – called after "Skip for now"
 *   onChecklistChange – called when a checklist item is toggled, receives updated array
 */
function WorkspaceOnboardingModal({
    config,
    state,
    workspaceId,
    customDomain,
    userInfo,
    onDismiss,
    onSkip,
    onChecklistChange,
    userRole = null,
    documentCount = null,
}) {
    const navigate = useNavigate();
    const [checklistCompleted, setChecklistCompleted] = useState(state?.checklist_completed || []);
    const [isSaving, setIsSaving] = useState(false);

    const API = "https://bluebirddocumentationadmin.pythonanywhere.com";

    const authHeaders = async () => {
        const token = await userInfo.getIdToken();
        return {
            "Content-Type": "application/json",
            Authorization: `${token}`,
            "Auth-Token": `Bearer ${token}`,
            "Requesting-User-Uid": userInfo.uid,
        };
    };

    const saveState = async (action, completedList) => {
        try {
            const headers = await authHeaders();
            const body = { checklist_completed: completedList };
            if (action) body.action = action;
            await fetch(`${API}/workspaces/${workspaceId}/onboarding/my-state/update/`, {
                method: "PATCH",
                headers,
                body: JSON.stringify(body),
            });
        } catch (e) {
            console.error("Failed to save onboarding state:", e);
        }
    };

    const handleChecklistToggle = async (itemId) => {
        const updated = checklistCompleted.includes(itemId)
            ? checklistCompleted.filter(id => id !== itemId)
            : [...checklistCompleted, itemId];
        setChecklistCompleted(updated);
        if (onChecklistChange) onChecklistChange(updated);
        await saveState(null, updated);
    };

    const handleGotIt = async () => {
        setIsSaving(true);
        await saveState("dismiss", checklistCompleted);
        onDismiss();
    };

    const handleSkip = async () => {
        await saveState("skip", checklistCompleted);
        onSkip();
    };

    const hasContent =
        config.welcome_message ||
        config.walkthrough ||
        (config.checklist_items && config.checklist_items.length > 0) ||
        (config.quick_links && config.quick_links.length > 0) ||
        config.point_of_contact;

    const completedCount = checklistCompleted.length;
    const totalItems = config.checklist_items ? config.checklist_items.length : 0;
    const allChecked = totalItems > 0 && completedCount === totalItems;

    return (
        <div className="WSOModal__overlay">
            <div className="WSOModal__panel">
                {/* Header */}
                <div className="WSOModal__header">
                    <div className="WSOModal__header-icon">
                        <i className="fas fa-rocket"></i>
                    </div>
                    <div>
                        <h2 className="WSOModal__title">Welcome to the workspace</h2>
                        <p className="WSOModal__subtitle">
                            Here's everything you need to get started.
                        </p>
                    </div>
                    <button
                        className="WSOModal__close-btn"
                        onClick={handleSkip}
                        title="Skip for now"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Body */}
                <div className="WSOModal__body">
                    {/* Empty workspace: offer the Team handbook kit to admins (BBD-110) */}
                    {documentCount === 0 && ['OWNER', 'ADMIN'].includes(userRole) && (
                        <section className="WSOModal__section">
                            <div className="WSOModal__section-header">
                                <i className="fas fa-puzzle-piece"></i>
                                <h3>Set up this workspace</h3>
                            </div>
                            <p style={{ margin: '0 0 10px' }}>
                                This workspace is empty. The Team handbook kit creates a "Start here" space with
                                Who does what, Tools and access, a First-week checklist, How we decide and a Glossary.
                            </p>
                            <button
                                type="button"
                                className="WSOModal__done-btn"
                                onClick={() => {
                                    onDismiss?.();
                                    navigate(`/workspace/${customDomain}/create/document?template=preset:team-handbook`);
                                }}
                            >
                                <i className="fas fa-book"></i> Set up with the Team handbook kit
                            </button>
                        </section>
                    )}
                    {!hasContent && (
                        <p className="WSOModal__empty">
                            No onboarding content has been configured yet.
                        </p>
                    )}

                    {/* Welcome message */}
                    {config.welcome_message && (
                        <section className="WSOModal__section">
                            <div
                                className="WSOModal__welcome-content ql-editor"
                                dangerouslySetInnerHTML={{ __html: config.welcome_message }}
                            />
                        </section>
                    )}

                    {/* Checklist */}
                    {config.checklist_items && config.checklist_items.length > 0 && (
                        <section className="WSOModal__section">
                            <div className="WSOModal__section-header">
                                <i className="fas fa-tasks"></i>
                                <h3>Getting Started Checklist</h3>
                                {totalItems > 0 && (
                                    <span className="WSOModal__checklist-progress">
                                        {completedCount}/{totalItems}
                                    </span>
                                )}
                            </div>
                            <div className="WSOModal__checklist">
                                {config.checklist_items.map(item => {
                                    const done = checklistCompleted.includes(item.id);
                                    return (
                                        <label key={item.id} className={`WSOModal__checklist-item${done ? " WSOModal__checklist-item--done" : ""}`}>
                                            <input
                                                type="checkbox"
                                                checked={done}
                                                onChange={() => handleChecklistToggle(item.id)}
                                            />
                                            <span>{item.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Documentation path */}
                    {config.walkthrough && (
                        <section className="WSOModal__section">
                            <div className="WSOModal__section-header">
                                <i className="fas fa-book-open"></i>
                                <h3>Recommended Reading</h3>
                            </div>
                            <button
                                className="WSOModal__walkthrough-btn"
                                onClick={() => navigate(`/workspace/${customDomain}/walkthroughs/${config.walkthrough.id}`)}
                            >
                                <i className="fas fa-map-signs"></i>
                                {config.walkthrough.name}
                                <i className="fas fa-arrow-right" style={{ marginLeft: "auto" }}></i>
                            </button>
                        </section>
                    )}

                    {/* Quick links */}
                    {config.quick_links && config.quick_links.length > 0 && (
                        <section className="WSOModal__section">
                            <div className="WSOModal__section-header">
                                <i className="fas fa-link"></i>
                                <h3>Quick Links</h3>
                            </div>
                            <div className="WSOModal__links">
                                {config.quick_links.map(link => (
                                    <a
                                        key={link.id}
                                        href={link.url}
                                        className="WSOModal__link-item"
                                        target={link.url.startsWith("http") ? "_blank" : undefined}
                                        rel="noopener noreferrer"
                                    >
                                        <i className="fas fa-external-link-alt"></i>
                                        {link.label}
                                    </a>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Point of contact */}
                    {config.point_of_contact && (
                        <section className="WSOModal__section">
                            <div className="WSOModal__section-header">
                                <i className="fas fa-user-headset"></i>
                                <h3>Your Onboarding Contact</h3>
                            </div>
                            <div className="WSOModal__poc">
                                <div className="WSOModal__poc-avatar">
                                    {(config.point_of_contact.name || config.point_of_contact.email).slice(0, 2).toUpperCase()}
                                </div>
                                <div className="WSOModal__poc-info">
                                    <span className="WSOModal__poc-name">
                                        {config.point_of_contact.name || config.point_of_contact.email}
                                    </span>
                                    {config.point_of_contact.position && (
                                        <span className="WSOModal__poc-position">
                                            {config.point_of_contact.position}
                                        </span>
                                    )}
                                    <span className="WSOModal__poc-email">
                                        {config.point_of_contact.email}
                                    </span>
                                </div>
                            </div>
                        </section>
                    )}
                </div>

                {/* Footer */}
                <div className="WSOModal__footer">
                    <button className="WSOModal__skip-btn" onClick={handleSkip}>
                        Skip for now
                    </button>
                    <button
                        className="WSOModal__done-btn"
                        onClick={handleGotIt}
                        disabled={isSaving}
                    >
                        {isSaving
                            ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                            : allChecked
                                ? <><i className="fas fa-check-circle"></i> All done!</>
                                : <><i className="fas fa-check"></i> Got it</>
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}

export default WorkspaceOnboardingModal;
